import React, { useEffect, useState } from 'react'
import Panel from '../../../components/Panel'
import type { TariffAuditEntityType } from '../../../apis/types'
import { formatDateTime } from '../../../util/string'
import { normalizeActorName } from '../../tenant/tenant/components/billing/incentiveDisplay'
import { useTariffAuditHistory } from '../useTariffAuditHistory'

const ENTITY_LABELS: Record<TariffAuditEntityType, string> = {
    TARIFF: 'Tariff',
    TARIFF_HISTORY: 'Rateset',
    TARIFF_RATE: 'Rate',
    TARIFF_RATE_SHEET: 'Rate sheet',
}

const COLUMNS = ['Action', 'Entity type', 'Entity', 'Changes', 'User', 'Timestamp', 'Reason'] as const

const cellClass = 'px-2 py-1 text-sm dark:text-gray-300'

export function formatAuditChangeLine(
    field: string,
    change: { before: unknown; after: unknown }
): string {
    return `${field}: ${String(change.before ?? '—')} → ${String(change.after ?? '—')}`
}

export const TariffActivityLogPanel: React.FC<{
    tariffId: string
    refreshKey?: number
}> = ({ tariffId, refreshKey = 0 }) => {
    const [page, setPage] = useState(0)
    const { auditHistory, total, isLoading } = useTariffAuditHistory(tariffId, page, refreshKey)

    useEffect(() => {
        setPage(0)
    }, [tariffId])
    const pageCount = Math.max(1, Math.ceil(total / 25))

    return (
        <Panel header="Activity log">
            <div id="tariff-activity-log" className="space-y-3">
                <div className="overflow-x-auto">
                    {isLoading && auditHistory.length === 0 ? (
                        <div className="animate-pulse space-y-2 py-2">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="flex gap-3 px-2 py-1">
                                    <div className="h-4 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    <div className="h-4 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    <div className="h-4 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    <div className="h-4 w-1/4 rounded bg-slate-200 dark:bg-slate-700" />
                                </div>
                            ))}
                        </div>
                    ) : auditHistory.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            No history available.
                        </p>
                    ) : (
                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                            <thead className="bg-white dark:bg-slate-950">
                                <tr>
                                    {COLUMNS.map((h) => (
                                        <th
                                            key={h}
                                            className={
                                                h === 'Changes'
                                                    ? 'py-1 pr-2 pl-0 text-left text-xs font-medium normal-case text-slate-500 dark:text-slate-400'
                                                    : 'px-2 py-1 text-left text-xs font-medium normal-case text-slate-500 dark:text-slate-400'
                                            }
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {auditHistory.map((entry) => {
                                    const changeEntries =
                                        entry.changes != null ? Object.entries(entry.changes) : []
                                    return (
                                        <tr
                                            key={entry.cuid}
                                            className="bg-slate-50 hover:bg-slate-100 dark:bg-slate-950/50 dark:hover:bg-white/[0.05]"
                                        >
                                            <td className={`${cellClass} font-medium dark:text-gray-200`}>
                                                {entry.action}
                                            </td>
                                            <td className={`${cellClass} whitespace-nowrap`}>
                                                <span className="inline-flex whitespace-nowrap rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300">
                                                    {ENTITY_LABELS[entry.entityType]}
                                                </span>
                                            </td>
                                            <td className={cellClass}>{entry.entityLabel}</td>
                                            <td className="py-1 pr-2 pl-0 text-xs text-slate-600 dark:text-slate-300">
                                                {changeEntries.length === 0 ? (
                                                    '—'
                                                ) : (
                                                    <ul className="list-none space-y-0.5 pl-0">
                                                        {changeEntries.map(([field, change]) => (
                                                            <li key={field}>
                                                                {formatAuditChangeLine(field, change)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                            <td className={cellClass}>
                                                {normalizeActorName(entry.user) ?? '—'}
                                            </td>
                                            <td className={cellClass}>
                                                {formatDateTime(new Date(entry.timestamp))}
                                            </td>
                                            <td className="px-2 py-1 text-sm text-gray-500 dark:text-gray-400">
                                                {entry.reason ?? '—'}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {pageCount > 1 ? (
                    <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
                        <button
                            type="button"
                            disabled={page === 0}
                            onClick={() => setPage((value) => Math.max(0, value - 1))}
                            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-50 dark:border-slate-600"
                        >
                            Previous
                        </button>
                        <span>
                            Page {page + 1} of {pageCount}
                        </span>
                        <button
                            type="button"
                            disabled={page + 1 >= pageCount}
                            onClick={() => setPage((value) => value + 1)}
                            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-50 dark:border-slate-600"
                        >
                            Next
                        </button>
                    </div>
                ) : null}
            </div>
        </Panel>
    )
}

export default TariffActivityLogPanel
